import { useQuery } from '@apollo/client';
import { Grid, Paper, Icon, Pagination } from '@mui/material';
import { ShibUser } from '@thro-types/users';

import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import QUERY from 'gql/queries/getUsers.gql';

import GQLGuard from 'app/components/GQLGuard';
import { buildListHeading, RichList } from 'app/components/RichList';
import { NoItems } from 'app/components/RichList/components/NoItems';
import { TestUserItem } from 'app/components/RichList/items/Users/TestUserItem';
import { actions as AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { UserType } from 'utils/ACLHelper';
import usePageHeading from 'utils/hooks/usePageHeading';

export function TestUserList() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [elementsPerPage] = useState(10);

  const testUserQuery = useQuery<
    any,
    {
      type?: string;
      dev?: boolean;
      application?: string;
      offset?: number;
      limit?: number;
    }
  >(QUERY, {
    fetchPolicy: 'no-cache',
    variables: {
      type: UserType.SHIBBOLETH,
      dev: true,
      offset: 0,
      limit: elementsPerPage
    },
    context: { clientName: 'federation' }
  });
  const { refetch } = testUserQuery;
  const handlePagination = (event: ChangeEvent<unknown>, page: number) => {
    const offset = (page - 1) * elementsPerPage;
    refetch({ offset: offset });
  };

  const getListHeading = () => {
    return buildListHeading(
      [
        { weight: 1, title: t(translations.common.id()) },
        { weight: 3, title: t(translations.common.email()) },
        { weight: 1, title: t(translations.common.semester()) },
        { weight: 7, title: 'Entitlements' }
      ],
      'users-management__list-heading__item'
    );
  };

  usePageHeading({
    heading: t(translations.layout.menu.testUserManagement()),
    mobilePadding: false
  });

  return (
    <GQLGuard
      query={testUserQuery}
      onError={error => {
        dispatch(
          AlertActions.error(`${t(translations.pages.shibUserList.alerts.loadUsersError())}`)
        );
        navigate('/', { replace: true });
        return null;
      }}
    >
      {(testUserData: { users: ShibUser[]; usersCount: number }) => {
        let usersCount = testUserData.usersCount;
        return (
          <Grid container className="users-management" component={Paper}>
            <RichList
              staticHeading={
                <Grid container className="users-management__list-heading" component={Paper} square>
                  {getListHeading()}
                </Grid>
              }
            >
              {testUserData.users.length === 0 ? (
                <NoItems icon={<Icon className="far fa-smile-beam" />} />
              ) : (
                <Grid container component={Paper} square>
                  {testUserData.users.map(user => (
                    <TestUserItem user={user} />
                  ))}
                </Grid>
              )}
            </RichList>
            {Math.floor(usersCount / elementsPerPage) > 0 && usersCount !== elementsPerPage && (
              <Grid container className="invoice-management__pagination">
                <Pagination
                  count={Math.ceil(usersCount / elementsPerPage)}
                  onChange={handlePagination}
                  defaultPage={1}
                  variant="outlined"
                  shape="rounded"
                  showFirstButton
                  showLastButton
                  size="medium"
                  color="primary"
                  siblingCount={2}
                  boundaryCount={2}
                />
              </Grid>
            )}
          </Grid>
        );
      }}
    </GQLGuard>
  );
}
